import { driver, type AllowedButtons, type DriveStep } from "driver.js"

export type TourStep = DriveStep

const HANDS_ON_BUTTONS: AllowedButtons[] = ["previous", "close"]

/**
 * A step where reading isn't enough — the tour won't move on until the
 * visitor actually performs the action on the *real* element (types in the
 * real search box, clicks the real menu). This is what makes the tour a
 * hands-on walkthrough instead of a narrated slideshow: if the element the
 * step points to doesn't exist or doesn't behave as described, the tour
 * just... doesn't advance, which is itself the signal that the flow is broken.
 *
 * `event: "click"` uses driver.js's built-in `advanceOnClick`. `event:
 * "input"` (typing) attaches a listener via `onHighlighted` and calls
 * `driver.moveNext()` once `minLength` characters have been typed.
 */
export function practicalStep(
  step: TourStep,
  event: "click" | "input" = "click",
  minLength = 1,
): TourStep {
  const popover = step.popover
    ? { ...step.popover, showButtons: HANDS_ON_BUTTONS }
    : undefined

  if (event === "click") {
    return { ...step, popover, advanceOnClick: true }
  }

  return {
    ...step,
    popover,
    onHighlighted: (element, _step, opts) => {
      if (!element) return
      const handler = () => {
        const value = (element as HTMLInputElement).value ?? ""
        if (value.trim().length < minLength) return
        element.removeEventListener("input", handler)
        opts.driver.moveNext()
      }
      element.addEventListener("input", handler)
    },
  }
}

export type RunTourOptions = {
  /** Fires once the tour closes, whether by finishing the last step or being closed early. */
  onDestroyed?: () => void
}

/**
 * Runs a guided tour, skipping any step whose `element` selector isn't
 * currently in the DOM (e.g. an empty table) instead of letting driver.js
 * throw. Gap-note steps (no `element`) always run.
 */
export function runTour(steps: TourStep[], options?: RunTourOptions) {
  const resolvedSteps = steps.filter((step) => {
    if (!step.element) return true
    const selector = typeof step.element === "string" ? step.element : null
    return selector ? document.querySelector(selector) !== null : true
  })

  if (resolvedSteps.length === 0) {
    options?.onDestroyed?.()
    return
  }

  const tour = driver({
    showProgress: true,
    animate: true,
    allowClose: true,
    overlayOpacity: 0.55,
    nextBtnText: "Siguiente",
    prevBtnText: "Atrás",
    doneBtnText: "Listo",
    progressText: "{{current}} de {{total}}",
    popoverClass: "gestion-tour-popover",
    steps: resolvedSteps,
    onDestroyed: options?.onDestroyed,
  })

  tour.drive()
}

/**
 * Polls for a selector to appear in the DOM, for waiting out an SPA route
 * change before running a tour on the page it navigates to. Resolves
 * `false` on timeout instead of rejecting — callers should treat that the
 * same as "step not applicable right now", not as a hard error.
 */
export function waitForElement(selector: string, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      resolve(true)
      return
    }
    const started = Date.now()
    const interval = window.setInterval(() => {
      if (document.querySelector(selector)) {
        window.clearInterval(interval)
        resolve(true)
      } else if (Date.now() - started > timeoutMs) {
        window.clearInterval(interval)
        resolve(false)
      }
    }, 100)
  })
}
